import { useMemo, useState } from "react";
import { useRouteLoaderData, useNavigate } from "react-router";
import { SidebarProvider } from "@/components/ui/sidebar";
import { WorkspaceSidebar } from "@/components/WorkspaceSidebar";
import { SettingsDialog } from "@/components/SettingsDialog";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Code as CodeIcon, User } from "lucide-react";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import {
	WORKSPACE_LOADER_ID,
	type WorkspaceLoaderData,
} from "@/routes/workspaceLoader";
import { useWorkspaceSidebarActions } from "@/hooks/useWorkspaceSidebarActions";

const AllCodes = () => {
	const navigate = useNavigate();
	const user = useAuthenticatedUser();
	const { codes, projects } = useRouteLoaderData(
		WORKSPACE_LOADER_ID,
	) as WorkspaceLoaderData;
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [showOnlyOwn, setShowOnlyOwn] = useState(false);
	const {
		createProject,
		createCode,
		updateProjectTitle,
		updateCodeTitle,
	} = useWorkspaceSidebarActions();

	const filteredCodes = useMemo(() => {
		if (!showOnlyOwn || !user?.id) return codes;
		return codes.filter((code) => code.user_id === user.id);
	}, [codes, showOnlyOwn, user?.id]);

	const handleCodeClick = (codeId: string) => {
		navigate(`/workspace/codes/${codeId}`);
	};

	return (
		<SidebarProvider>
			<div className="min-h-screen flex w-full">
				<WorkspaceSidebar
					projects={projects}
					codes={filteredCodes}
					onNewProject={createProject}
					onNewCode={createCode}
					onUpdateProjectTitle={updateProjectTitle}
					onUpdateCodeTitle={updateCodeTitle}
					onOpenSettings={() => setSettingsOpen(true)}
				/>
				<main className="flex-1 overflow-auto">
					<div className="container mx-auto py-8 px-4">
						<div className="mb-6 flex items-start justify-between">
							<div>
								<h1 className="text-4xl font-bold text-foreground">
									All Agents
								</h1>
								<p className="text-muted-foreground mt-2">
									Browse and select from all available agents
								</p>
							</div>
							<div className="flex items-center gap-4">
								<div className="flex items-center space-x-2">
									<Switch
										id="show-only-own"
										checked={showOnlyOwn}
										onCheckedChange={(checked) =>
											setShowOnlyOwn(Boolean(checked))
										}
									/>
									<Label
										htmlFor="show-only-own"
										className="text-sm font-medium cursor-pointer"
									>
										Only my agents
									</Label>
								</div>
								<Button onClick={() => createCode()} size="lg">
									<CodeIcon className="mr-2 h-4 w-4" />
									Create Agent
								</Button>
							</div>
						</div>

						{filteredCodes.length === 0 ? (
							<Card>
								<CardContent className="py-12 text-center">
									<CodeIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
									<p className="text-muted-foreground">No agents found</p>
								</CardContent>
							</Card>
						) : (
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
								{filteredCodes.map((code) => (
									<Card
										key={code.id}
										className="cursor-pointer hover:border-primary transition-colors"
										data-testid={`code-card-${code.id}`}
										onClick={() => handleCodeClick(code.id)}
									>
										<CardHeader>
											<div className="flex items-start justify-between">
												<div className="flex items-center gap-2">
													<CodeIcon className="h-5 w-5 text-primary" />
													<CardTitle className="text-lg">
														{code.title}
													</CardTitle>
												</div>
												{code.is_public && (
													<Badge variant="secondary">Public</Badge>
												)}
											</div>
											<CardDescription className="flex items-center gap-1 mt-2">
												<User className="h-3 w-3" />
												<span className="text-xs">
													{code.user_id === user?.id ? "You" : code.owner_email}
												</span>
											</CardDescription>
										</CardHeader>
										<CardContent>
											<div className="space-y-2">
												<div>
													<span className="text-xs font-semibold text-muted-foreground">
														Language:
													</span>
													<p className="text-sm">JavaScript</p>
												</div>
												<div>
													<span className="text-xs font-semibold text-muted-foreground">
														Preview:
													</span>
													<pre className="text-xs line-clamp-3 bg-muted p-2 rounded mt-1 overflow-hidden">
														{code.code_content}
													</pre>
												</div>
											</div>
										</CardContent>
									</Card>
								))}
							</div>
						)}
					</div>
				</main>
				<SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
			</div>
		</SidebarProvider>
	);
};

export default AllCodes;
